/**
 * Customer Detail Page
 * 
 * Comprehensive customer view with behavior tracking and cart visibility.
 * Displays customer profile, active cart, product/catalogue view history,
 * order history, and engagement timeline.
 */
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Tab,
  Tabs,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  ShoppingCart as CartIcon,
  Visibility as ViewIcon,
  Category as CategoryIcon,
  Receipt as OrderIcon,
  Edit as EditIcon,
  ArrowBack as BackIcon,
  TrendingUp as TrendingIcon
} from '@mui/icons-material';
import { CustomerService } from '../services/customerService';
import { BehaviorService } from '../services/behaviorService';
import { CartService } from '../services/cartService';
import { Customer } from '../types/customer';
import { CartDetail } from '../types/activeCart';
import { CustomerJourneyEvent } from '../types/customerBehavior';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`customer-tabpanel-${index}`}
      aria-labelledby={`customer-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export const CustomerDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  // "null" (string) arrives when navigating from a guest-session row; treat it as missing
  const validId = !!id && id !== 'null' && id !== 'undefined';

  // Fetch customer profile
  const { data: customer, isLoading: customerLoading, error: customerError } = useQuery<Customer>({
    queryKey: ['customer', id],
    queryFn: () => CustomerService.getCustomer(id!),
    enabled: validId,
    staleTime: 5 * 60 * 1000 // 5 minutes
  });

  // Fetch customer's active cart
  const { data: activeCart, isLoading: cartLoading } = useQuery<CartDetail | null>({
    queryKey: ['customer-cart', id],
    queryFn: () => CartService.getCustomerCart(id!),
    enabled: validId,
    staleTime: 1 * 60 * 1000 // 1 minute for real-time cart data
  });

  // Fetch customer journey (all activities)
  const { data: journeyData, isLoading: journeyLoading } = useQuery({
    queryKey: ['customer-journey', id],
    queryFn: () => BehaviorService.getCustomerJourney(id!, { period: '30d' }),
    enabled: validId,
    staleTime: 5 * 60 * 1000
  });

  // Fetch product view history
  const { data: productViews, isLoading: productViewsLoading } = useQuery({
    queryKey: ['product-views', id],
    queryFn: () => BehaviorService.getProductViews({ customer_id: id, period: '30d', limit: 50 }),
    enabled: validId,
    staleTime: 5 * 60 * 1000
  });

  // Fetch catalogue view history
  const { data: catalogueViews, isLoading: catalogueViewsLoading } = useQuery({
    queryKey: ['catalogue-views', id],
    queryFn: () => BehaviorService.getCatalogueViews({ customer_id: id, period: '30d', limit: 50 }),
    enabled: validId,
    staleTime: 5 * 60 * 1000
  });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleBack = () => {
    navigate('/admin/parties/customers');
  };

  const handleEdit = () => {
    navigate(`/admin/parties/customers/${id}/edit`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const getEventIcon = (eventType: string) => {
    switch (eventType) {
      case 'product_view':
        return <ViewIcon />;
      case 'catalogue_view':
        return <CategoryIcon />;
      case 'cart_add':
        return <CartIcon />;
      case 'order':
        return <OrderIcon />;
      default:
        return <TrendingIcon />;
    }
  };

  const getEventColor = (eventType: string): "primary" | "secondary" | "success" | "info" => {
    switch (eventType) {
      case 'product_view':
        return 'info';
      case 'catalogue_view':
        return 'secondary';
      case 'cart_add':
        return 'primary';
      case 'order':
        return 'success';
      default:
        return 'info';
    }
  };

  if (!validId) {
    return (
      <Box p={3}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={() => navigate('/admin/parties/customers')}>
            Back to Customers
          </Button>
        }>
          Invalid customer ID. Guest sessions do not have a customer profile.
        </Alert>
      </Box>
    );
  }

  if (customerLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (customerError || !customer) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Failed to load customer details. Please try again.
        </Alert>
        <Button startIcon={<BackIcon />} onClick={handleBack} sx={{ mt: 2 }}>
          Back to Customers
        </Button>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={handleBack}>
            <BackIcon />
          </IconButton>
          <Typography variant="h4">Customer Details</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={handleEdit}
        >
          Edit Customer
        </Button>
      </Box>

      {/* Customer Profile Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <PersonIcon color="primary" />
                <Typography variant="h6">{customer.name}</Typography>
                <Chip
                  label={customer.customer_type}
                  color="primary"
                  size="small"
                  sx={{ ml: 1 }}
                />
                {!customer.is_active && (
                  <Chip label="Inactive" color="error" size="small" />
                )}
              </Box>
              
              <Box display="flex" flexDirection="column" gap={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  <EmailIcon fontSize="small" color="action" />
                  <Typography variant="body2">{customer.email}</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <PhoneIcon fontSize="small" color="action" />
                  <Typography variant="body2">{customer.mobile_no}</Typography>
                </Box>
                {customer.phone && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <PhoneIcon fontSize="small" color="action" />
                    <Typography variant="body2">{customer.phone}</Typography>
                  </Box>
                )}
                {customer.gstin && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <BusinessIcon fontSize="small" color="action" />
                    <Typography variant="body2">GSTIN: {customer.gstin}</Typography>
                  </Box>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Account Information
              </Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                <Typography variant="body2">
                  <strong>Credit Limit:</strong> {formatCurrency(customer.credit_limit)}
                </Typography>
                <Typography variant="body2">
                  <strong>Credit Days:</strong> {customer.credit_days} days
                </Typography>
                {customer.customer_group && (
                  <Typography variant="body2">
                    <strong>Group:</strong> {customer.customer_group.name}
                  </Typography>
                )}
                {customer.last_login && (
                  <Typography variant="body2">
                    <strong>Last Login:</strong> {formatDate(customer.last_login)}
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Active Cart Section */}
      {cartLoading ? (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress size={24} />
            </Box>
          </CardContent>
        </Card>
      ) : activeCart ? (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <CartIcon color="primary" />
                <Typography variant="h6">Active Cart</Typography>
                <Chip
                  label={`${activeCart.items.length} items`}
                  size="small"
                  color="primary"
                />
              </Box>
              <Typography variant="h6" color="primary">
                {formatCurrency(activeCart.total_value)}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell align="right">Unit Price</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="center">Stock</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeCart.items.map((item) => (
                    <TableRow key={item.product_variant_id}>
                      <TableCell>
                        <Typography variant="body2">{item.product_name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          SKU: {item.sku}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{item.quantity}</TableCell>
                      <TableCell align="right">{formatCurrency(item.unit_price)}</TableCell>
                      <TableCell align="right">{formatCurrency(item.line_total)}</TableCell>
                      <TableCell align="center">
                        {item.is_in_stock ? (
                          <Chip label="In Stock" color="success" size="small" />
                        ) : (
                          <Chip label="Out of Stock" color="error" size="small" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Last updated: {formatDate(activeCart.last_activity)}
              </Typography>
              <Box display="flex" gap={1}>
                <Typography variant="body2">
                  <strong>Subtotal:</strong> {formatCurrency(activeCart.subtotal)}
                </Typography>
                <Typography variant="body2">
                  <strong>Tax:</strong> {formatCurrency(activeCart.tax_amount)}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Alert severity="info">
              This customer does not have an active cart.
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Tabs for different sections */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="Engagement Timeline" />
            <Tab label="Product Views" />
            <Tab label="Catalogue Views" />
          </Tabs>
        </Box>

        {/* Engagement Timeline Tab */}
        <TabPanel value={activeTab} index={0}>
          {journeyLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : journeyData && journeyData.events.length > 0 ? (
            <List>
              {journeyData.events.map((event: CustomerJourneyEvent) => (
                <ListItem key={event.event_id} sx={{ mb: 2 }}>
                  <Paper elevation={2} sx={{ p: 2, width: '100%' }}>
                    <Box display="flex" alignItems="center" gap={2} mb={1}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Chip
                          icon={getEventIcon(event.event_type)}
                          label={event.event_type.replace('_', ' ').toUpperCase()}
                          color={getEventColor(event.event_type)}
                          size="small"
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(event.event_timestamp)}
                          </Typography>
                        }
                      />
                    </Box>
                    {event.event_data && Object.keys(event.event_data).length > 0 && (
                      <Box mt={1} pl={2}>
                        {event.event_data.product_name && (
                          <Typography variant="body2">
                            <strong>Product:</strong> {event.event_data.product_name}
                          </Typography>
                        )}
                        {event.event_data.catalogue_name && (
                          <Typography variant="body2">
                            <strong>Catalogue:</strong> {event.event_data.catalogue_name}
                          </Typography>
                        )}
                        {event.event_data.order_id && (
                          <Typography variant="body2">
                            <strong>Order:</strong> {event.event_data.order_id}
                          </Typography>
                        )}
                        {event.event_data.quantity && (
                          <Typography variant="body2">
                            <strong>Quantity:</strong> {event.event_data.quantity}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Paper>
                </ListItem>
              ))}
            </List>
          ) : (
            <Alert severity="info">
              No activity recorded for this customer in the last 30 days.
            </Alert>
          )}
        </TabPanel>

        {/* Product Views Tab */}
        <TabPanel value={activeTab} index={1}>
          {productViewsLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : productViews && productViews.views.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell>SKU</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Viewed At</TableCell>
                    <TableCell align="right">Duration (sec)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {productViews.views.map((view) => (
                    <TableRow key={`${view.product_id}-${view.view_timestamp}`}>
                      <TableCell>{view.product_name}</TableCell>
                      <TableCell>{view.product_sku}</TableCell>
                      <TableCell>{view.category || 'N/A'}</TableCell>
                      <TableCell>{formatDate(view.view_timestamp)}</TableCell>
                      <TableCell align="right">
                        {view.view_duration_seconds || 'N/A'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">
              No product views recorded for this customer in the last 30 days.
            </Alert>
          )}
        </TabPanel>

        {/* Catalogue Views Tab */}
        <TabPanel value={activeTab} index={2}>
          {catalogueViewsLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : catalogueViews && catalogueViews.views.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Catalogue</TableCell>
                    <TableCell align="right">View Count</TableCell>
                    <TableCell align="right">Unique Customers</TableCell>
                    <TableCell align="right">Avg Time (min)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {catalogueViews.views.map((view) => (
                    <TableRow key={view.catalogue_id}>
                      <TableCell>{view.catalogue_name}</TableCell>
                      <TableCell align="right">{view.view_count}</TableCell>
                      <TableCell align="right">{view.unique_customer_count}</TableCell>
                      <TableCell align="right">
                        {view.average_time_spent_seconds ? Math.round(view.average_time_spent_seconds / 60) : 'N/A'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">
              No catalogue views recorded for this customer in the last 30 days.
            </Alert>
          )}
        </TabPanel>
      </Card>
    </Box>
  );
};

export default CustomerDetailPage;
